import React, { useState, useMemo } from 'react';
import { Select, Space, Typography, Image, Divider, Empty } from 'antd';
import { SourceProcess, SourceScene, SourcePanel } from '../../types';
import { getPanelReferenceImageUrls } from '../../utils/panelReferenceImages';

const { Text } = Typography;
const { Option } = Select;

interface HierarchicalPanelPickerProps {
  value?: string;
  onChange?: (value: string) => void;
  processes: SourceProcess[];
  scenes: SourceScene[];
  panels: SourcePanel[];
  placeholder?: string;
  loading?: boolean;
  initialProcessId?: string;
  initialSceneId?: string;
}

const HierarchicalPanelPicker: React.FC<HierarchicalPanelPickerProps> = ({
  value,
  onChange,
  processes,
  scenes,
  panels,
  placeholder = "Select a panel",
  loading = false,
  initialProcessId,
  initialSceneId,
}) => {
  const [selectedProcessId, setSelectedProcessId] = useState<string | undefined>(initialProcessId);
  const [selectedSceneId, setSelectedSceneId] = useState<string | undefined>(initialSceneId);

  // Find current panel hierarchy if value exists
  const currentHierarchy = useMemo(() => {
    if (!value) return null;
    const panel = panels.find(p => p.id === value);
    if (!panel) return null;
    const scene = scenes.find(s => s.id === panel.sceneId);
    if (!scene) return { panel };
    const process = processes.find(p => p.id === scene.processId);
    return { panel, scene, process };
  }, [value, panels, scenes, processes]);

  // Derived data
  const filteredScenes = useMemo(() => {
    if (!selectedProcessId) return [];
    return scenes.filter(s => s.processId === selectedProcessId).sort((a, b) => a.orderIndex - b.orderIndex);
  }, [selectedProcessId, scenes]);

  const filteredPanels = useMemo(() => {
    if (!selectedSceneId) return [];
    return panels.filter(p => p.sceneId === selectedSceneId).sort((a, b) => a.sceneOrderIndex - b.sceneOrderIndex);
  }, [selectedSceneId, panels]);

  // Auto-set process and scene if value is provided from outside (initial load)
  React.useEffect(() => {
    if (currentHierarchy) {
      if (currentHierarchy.process && !selectedProcessId) {
        setSelectedProcessId(currentHierarchy.process.id);
      }
      if (currentHierarchy.scene && !selectedSceneId) {
        setSelectedSceneId(currentHierarchy.scene.id);
      }
    }
  }, [currentHierarchy, selectedProcessId, selectedSceneId]);

  const handleProcessChange = (val: string) => {
    setSelectedProcessId(val);
    setSelectedSceneId(undefined);
  };

  const handleSceneChange = (val: string) => {
    setSelectedSceneId(val);
  };

  const handlePanelChange = (val: string) => {
    if (onChange) {
      onChange(val);
    }
  };

  return (
    <div style={{ border: '1px solid #f0f0f0', padding: '12px', borderRadius: '4px', background: '#fafafa' }}>
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        <div style={{ display: 'flex', gap: '8px' }}>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>1. Select Process</Text>
            <Select
              style={{ width: '100%' }}
              placeholder="Select Process"
              value={selectedProcessId}
              onChange={handleProcessChange}
              loading={loading}
              showSearch
              optionFilterProp="children"
            >
              {processes.map(p => (
                <Option key={p.id} value={p.id}>{p.name}</Option>
              ))}
            </Select>
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>2. Select Scene</Text>
            <Select
              style={{ width: '100%' }}
              placeholder="Select Scene"
              value={selectedSceneId}
              onChange={handleSceneChange}
              disabled={!selectedProcessId}
              showSearch
              optionFilterProp="children"
            >
              {filteredScenes.map(s => (
                <Option key={s.id} value={s.id}>#{s.orderIndex}: {s.title}</Option>
              ))}
            </Select>
          </div>
        </div>

        <div>
          <Text type="secondary" style={{ fontSize: '12px' }}>3. Select Panel</Text>
          <Select
            style={{ width: '100%' }}
            placeholder={placeholder}
            value={value}
            onChange={handlePanelChange}
            disabled={!selectedSceneId}
            listHeight={400}
            dropdownStyle={{ width: 450 }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) => 
              (option?.children as any)?.props?.children[1]?.props?.children?.toLowerCase().includes(input.toLowerCase()) || false
            }
          >
            {filteredPanels.map(p => {
              const thumbs = getPanelReferenceImageUrls(p);
              return (
              <Option key={p.id} value={p.id}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '4px 0' }}>
                  <div style={{ 
                    width: thumbs.length > 1 ? 68 : 60, 
                    height: 100, 
                    flexShrink: 0, 
                    background: '#eee', 
                    borderRadius: 4, 
                    overflow: 'hidden', 
                    display: 'flex',
                    gap: 2,
                  }}>
                    {thumbs.length > 0 ? (
                      thumbs.map((url, ti) => (
                        <img key={ti} src={url} alt="" style={{ flex: 1, minWidth: 0, height: '100%', objectFit: 'cover' }} />
                      ))
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={false} style={{ margin: 0 }} />
                      </div>
                    )}
                  </div>
                  <div style={{ flex: 1, overflow: 'hidden' }}>
                    <div style={{ fontWeight: 'bold' }}>Panel #{p.sceneOrderIndex}</div>
                    <div style={{ fontSize: '12px', color: '#666', whiteSpace: 'normal', lineHeight: '1.4' }}>
                      {p.caption}
                    </div>
                  </div>
                </div>
              </Option>
            );
            })}
          </Select>
        </div>

        {currentHierarchy && (
          <div style={{ marginTop: 8, padding: '8px', background: '#e6f7ff', borderRadius: 4, border: '1px solid #91d5ff' }}>
            <Text strong style={{ fontSize: '12px' }}>Selected: </Text>
            <Text style={{ fontSize: '12px' }}>
              {currentHierarchy.process?.name || 'Unknown Process'} 
              {' > '} 
              Scene #{currentHierarchy.scene ? currentHierarchy.scene.orderIndex : '?'}: {currentHierarchy.scene?.title || 'Unknown Scene'}
              {' > '}
              Panel #{currentHierarchy.panel?.sceneOrderIndex || '?'}
            </Text>
          </div>
        )}
      </Space>
    </div>
  );
};

export default HierarchicalPanelPicker;
